import { useState } from 'react';
import { useShallow } from 'zustand/shallow';

import { useAppStore } from '@/store/app.store';
import { useAssetEditStore } from '@/store/asset-edit.store';
import Button from '@ingka/button';
import SegmentedControl from '@ingka/segmented-control';
import pencilIcon from '@ingka/ssr-icon/paths/pencil';
import { translate } from '@repo/phrase';

import { useAssetParams } from '@/hooks/asset.hooks';
import { useIsEditModeAvailable } from '@/hooks/edit-mode.hooks';

import { AssetReviewChangesModal } from './asset-review-changes-modal.component';

import styles from './styles.module.scss';

export const AssetDetailsMode = () => {
    const { assetId } = useAssetParams();
    const [isReviewChangesModalVisible, setIsReviewChangesModalVisible] = useState(false);
    const {
        activeAssetDetailsView,
        enterAssetDetailsEditMode,
        exitAssetDetailsEditMode,
        isAssetDetailsEditMode,
        setAssetDetailsView,
    } = useAppStore(
        useShallow((state) => ({
            activeAssetDetailsView: state.assetDetails.view,
            enterAssetDetailsEditMode: state.enterAssetDetailsEditMode,
            exitAssetDetailsEditMode: state.exitAssetDetailsEditMode,
            isAssetDetailsEditMode: state.assetDetails.mode === 'edit',
            setAssetDetailsView: state.setAssetDetailsView,
        }))
    );
    const hasPendingSubmissions = useAssetEditStore(
        (state) => state.assetId === assetId && state.pendingSubmissions.length > 0
    );
    const isEditModeAvailable = useIsEditModeAvailable() && activeAssetDetailsView === 'fulfill';
    const isEditButtonDisabled = !isAssetDetailsEditMode && !isEditModeAvailable;

    const handleEditClick = () => {
        if (isAssetDetailsEditMode) {
            if (hasPendingSubmissions) {
                setIsReviewChangesModalVisible(true);
                return;
            }

            exitAssetDetailsEditMode();
            return;
        }

        if (!isEditModeAvailable) {
            return;
        }

        enterAssetDetailsEditMode();
    };

    const editButtonText = isAssetDetailsEditMode
        ? translate(hasPendingSubmissions ? 'asset_details_mode_review_changes' : 'asset_details_mode_cancel')
        : translate('asset_details_mode_edit');

    return (
        <>
            <div className={styles.asset_details_mode}>
                <div className={styles.asset_details_mode_control}>
                    <SegmentedControl
                        size="small"
                        activeIndex={activeAssetDetailsView === 'fulfill' ? 1 : 0}
                        items={[
                            { label: translate('asset_details_mode_organize') },
                            { label: translate('asset_details_mode_fulfill') },
                        ]}
                        onClick={(_, index) => {
                            setAssetDetailsView(index === 1 ? 'fulfill' : 'organize');
                        }}
                    />
                </div>
                {!isEditButtonDisabled && (
                    <Button
                        className={String(styles.asset_details_mode_edit_button)}
                        size="small"
                        type="secondary"
                        text={editButtonText}
                        onClick={handleEditClick}
                        {...(isAssetDetailsEditMode ? {} : { ssrIcon: pencilIcon })}
                    />
                )}
            </div>
            <AssetReviewChangesModal
                visible={isReviewChangesModalVisible}
                onClose={() => {
                    setIsReviewChangesModalVisible(false);
                }}
            />
        </>
    );
};
